import { differenceInLocalDays, isValidLocalDate, toLocalDate, type LocalDate } from './local-date';

export type DueTone = 'today' | 'upcoming' | 'overdue' | 'waiting';

export interface DueSummary {
  tone: DueTone;
  /** 卡片正面顯示的剩餘天數文字，不直接顯示日期字串（spec 13.5）。 */
  label: string;
  /** tooltip 用的完整日期。 */
  fullDate: LocalDate;
}

export interface DueSummaryInput {
  /** 判斷基準日，一律由呼叫端傳入，方便測試與跨午夜重算。 */
  today: LocalDate;
  dueDate?: LocalDate | null;
  status?: string | null;
  /** UTC 稽核時間；等待回覆的起算點。 */
  waitingSince?: string | null;
}

function utcTimestampToLocalDate(value: string): LocalDate | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return toLocalDate(parsed);
}

/**
 * 產生卡片與清單共用的到期摘要。
 * 等待回覆優先於到期日；兩者皆無時回傳 null，由呼叫端隱藏整個區塊。
 */
export function describeDueSummary(input: DueSummaryInput): DueSummary | null {
  const { today, dueDate, status, waitingSince } = input;

  if (status === 'waiting' && waitingSince) {
    const since = utcTimestampToLocalDate(waitingSince);
    if (since) {
      const waited = Math.max(0, differenceInLocalDays(today, since));
      return { tone: 'waiting', label: `已等 ${waited} 天`, fullDate: since };
    }
  }

  if (!dueDate || !isValidLocalDate(dueDate)) return null;

  const remaining = differenceInLocalDays(dueDate, today);
  if (remaining === 0) {
    return { tone: 'today', label: '今天到期', fullDate: dueDate };
  }
  if (remaining > 0) {
    return { tone: 'upcoming', label: `剩 ${remaining} 天`, fullDate: dueDate };
  }
  return { tone: 'overdue', label: `逾期 ${Math.abs(remaining)} 天`, fullDate: dueDate };
}
